import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { themes, type Theme, type ThemeId, type ThemeType, generateCSSVariables, resolveSystemTheme } from '../config/themes'

interface ThemeContextType {
  theme: ThemeType
  setTheme: (theme: ThemeType) => void
  actualTheme: 'light' | 'dark'
  activeTheme: Theme
  availableThemes: Record<ThemeId, Theme>
  isTransitioning: boolean
  toggleTheme: () => void
  setCustomTheme: (theme: Partial<Theme>) => void
  resetTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ThemeType>(() => {
    const stored = localStorage.getItem('carevault-theme') as ThemeType
    return stored || 'system'
  })

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [customTheme, setCustomThemeState] = useState<Partial<Theme> | null>(null)

  // Get the active theme with custom overrides if any
  const activeTheme: Theme = React.useMemo(() => {
    const baseTheme = themes[actualTheme]
    if (!customTheme) return baseTheme

    // Merge custom theme with base theme
    return {
      ...baseTheme,
      ...customTheme,
      colors: {
        ...baseTheme.colors,
        ...customTheme.colors
      },
      components: {
        ...baseTheme.components,
        ...customTheme.components
      }
    }
  }, [actualTheme, customTheme])

  // Apply CSS variables to the document
  const applyCSSVariables = useCallback((theme: Theme) => {
    const variables = generateCSSVariables(theme)
    const root = document.documentElement

    Object.entries(variables).forEach(([property, value]) => {
      root.style.setProperty(property, value)
    })
  }, [])

  // Handle smooth theme transitions
  const enableTransitions = useCallback(() => {
    setIsTransitioning(true)
    document.documentElement.classList.add('theme-transition')

    // Remove transition class after animation completes
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition')
      setIsTransitioning(false)
    }, 300)
  }, [])

  // Update theme with transitions
  const updateTheme = useCallback((newTheme: ThemeType, withTransition = true) => {
    if (withTransition) {
      enableTransitions()
    }

    let resolvedTheme: 'light' | 'dark'

    if (newTheme === 'system') {
      resolvedTheme = resolveSystemTheme()
    } else {
      resolvedTheme = newTheme
    }

    setActualTheme(resolvedTheme)

    // Update DOM classes
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)

    // Apply CSS variables
    applyCSSVariables(themes[resolvedTheme])
  }, [enableTransitions, applyCSSVariables])

  // Enhanced setTheme with transition support
  const setThemeWithTransition = useCallback((newTheme: ThemeType) => {
    setTheme(newTheme)
    updateTheme(newTheme, true)
  }, [updateTheme])

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    const newTheme = actualTheme === 'light' ? 'dark' : 'light'
    setThemeWithTransition(newTheme)
  }, [actualTheme, setThemeWithTransition])

  // Set custom theme overrides
  const setCustomTheme = useCallback((customOverrides: Partial<Theme>) => {
    setCustomThemeState(customOverrides)
    // Apply the custom theme immediately
    const mergedTheme = {
      ...themes[actualTheme],
      ...customOverrides,
      colors: {
        ...themes[actualTheme].colors,
        ...customOverrides.colors
      },
      components: {
        ...themes[actualTheme].components,
        ...customOverrides.components
      }
    }
    applyCSSVariables(mergedTheme)
  }, [actualTheme, applyCSSVariables])

  // Reset to default theme
  const resetTheme = useCallback(() => {
    setCustomThemeState(null)
    applyCSSVariables(themes[actualTheme])
  }, [actualTheme, applyCSSVariables])

  // Handle system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleSystemThemeChange = (_: MediaQueryListEvent) => {
      if (theme === 'system') {
        updateTheme('system', true)
      }
    }

    if (theme === 'system') {
      mediaQuery.addEventListener('change', handleSystemThemeChange)
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [theme, updateTheme])

  // Initialize theme on mount
  useEffect(() => {
    updateTheme(theme, false) // No transition on initial load
  }, []) // Run only once on mount

  // Update actual theme when theme preference changes
  useEffect(() => {
    updateTheme(theme, true)
  }, [theme, updateTheme])

  // Persist theme preference
  useEffect(() => {
    localStorage.setItem('carevault-theme', theme)
  }, [theme])

  // Handle custom theme changes
  useEffect(() => {
    if (customTheme) {
      applyCSSVariables(activeTheme)
    }
  }, [customTheme, activeTheme, applyCSSVariables])

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme: setThemeWithTransition,
      actualTheme,
      activeTheme,
      availableThemes: themes,
      isTransitioning,
      toggleTheme,
      setCustomTheme,
      resetTheme
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Hook for theme-aware component styling
export function useThemeStyles() {
  const { activeTheme, actualTheme } = useTheme()

  return {
    theme: activeTheme,
    isDark: actualTheme === 'dark',
    isLight: actualTheme === 'light',
    colors: activeTheme.colors,
    components: activeTheme.components
  }
}

// Hook for conditional theme classes
export function useThemeClasses() {
  const { actualTheme } = useTheme()

  return {
    isDark: actualTheme === 'dark',
    isLight: actualTheme === 'light',
    themeClass: actualTheme,
    // Helper functions for conditional classes
    light: (classes: string) => actualTheme === 'light' ? classes : '',
    dark: (classes: string) => actualTheme === 'dark' ? classes : '',
    theme: (lightClasses: string, darkClasses: string) =>
      actualTheme === 'light' ? lightClasses : darkClasses
  }
}